import api from "../../../api/axios";

// Feature-local Deal transport wrappers.
// Typed A2 create + lifecycle paths only — generic POST /realty/deals/ is closed (405).
// C1 transition-status is retained as a named helper for compatibility tests only;
// production Deal workspace UI must use typed accept/decline/withdraw/cancel/close.

export const fetchDeals = (params = {}, { signal } = {}) =>
  api.get("/realty/deals/", { params, signal }).then((res) => res.data);

export const fetchDealById = (dealId, { signal } = {}) =>
  api.get(`/realty/deals/${dealId}/`, { signal }).then((res) => res.data);

/**
 * @deprecated C1 compatibility shim — not the primary C2 lifecycle UI path.
 * Prefer acceptDeal / declineDeal / withdrawDeal / cancelDeal / closeDeal.
 */
export const transitionDealStatus = (
  dealId,
  expectedStatus,
  targetStatus,
  { signal } = {},
) =>
  api
    .post(
      `/realty/deals/${dealId}/transition-status/`,
      {
        expected_status: expectedStatus,
        target_status: targetStatus,
      },
      { signal },
    )
    .then((res) => res.data);

/**
 * External proposal — POST /api/realty/deals/propose/
 */
export const proposeDeal = (payload, { signal } = {}) =>
  api
    .post(
      "/realty/deals/propose/",
      {
        property: payload.property,
        client_id: payload.client_id,
        price: payload.price,
        currency: payload.currency,
        ...(payload.proposal_note != null
          ? { proposal_note: payload.proposal_note }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(payload, "property_share")
          ? { property_share: payload.property_share }
          : {}),
      },
      { signal },
    )
    .then((res) => res.data);

/**
 * Internal Deal — POST /api/realty/deals/start-internal/
 */
export const startInternalDeal = (payload, { signal } = {}) =>
  api
    .post(
      "/realty/deals/start-internal/",
      {
        property: payload.property,
        client_id: payload.client_id,
        price: payload.price,
        currency: payload.currency,
        ...(payload.proposal_note != null
          ? { proposal_note: payload.proposal_note }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(payload, "property_share")
          ? { property_share: payload.property_share }
          : {}),
      },
      { signal },
    )
    .then((res) => res.data);

/** POST /api/realty/deals/{id}/accept/ — body: { expected_status } */
export const acceptDeal = (dealId, expectedStatus, { signal } = {}) =>
  api
    .post(
      `/realty/deals/${dealId}/accept/`,
      { expected_status: expectedStatus },
      { signal },
    )
    .then((res) => res.data);

/** POST /api/realty/deals/{id}/decline/ — body: { expected_status, reason? } */
export const declineDeal = (
  dealId,
  expectedStatus,
  { reason, signal } = {},
) =>
  api
    .post(
      `/realty/deals/${dealId}/decline/`,
      {
        expected_status: expectedStatus,
        ...(reason != null && String(reason).trim()
          ? { reason: String(reason).trim() }
          : {}),
      },
      { signal },
    )
    .then((res) => res.data);

/** POST /api/realty/deals/{id}/withdraw/ — body: { expected_status, reason? } */
export const withdrawDeal = (
  dealId,
  expectedStatus,
  { reason, signal } = {},
) =>
  api
    .post(
      `/realty/deals/${dealId}/withdraw/`,
      {
        expected_status: expectedStatus,
        ...(reason != null && String(reason).trim()
          ? { reason: String(reason).trim() }
          : {}),
      },
      { signal },
    )
    .then((res) => res.data);

/** POST /api/realty/deals/{id}/cancel/ — body: { expected_status, reason } required */
export const cancelDeal = (dealId, expectedStatus, reason, { signal } = {}) =>
  api
    .post(
      `/realty/deals/${dealId}/cancel/`,
      {
        expected_status: expectedStatus,
        reason: String(reason || "").trim(),
      },
      { signal },
    )
    .then((res) => res.data);

/** POST /api/realty/deals/{id}/close/ — body: { expected_status } */
export const closeDeal = (dealId, expectedStatus, { signal } = {}) =>
  api
    .post(
      `/realty/deals/${dealId}/close/`,
      { expected_status: expectedStatus },
      { signal },
    )
    .then((res) => res.data);

/**
 * G1/G2 — POST /api/realty/deals/{id}/reassign-representative/
 * Body: { side: "seller"|"buyer", representative_id: number }
 */
export const reassignDealRepresentative = (
  dealId,
  { side, representativeId },
  { signal } = {},
) =>
  api
    .post(
      `/realty/deals/${dealId}/reassign-representative/`,
      {
        side,
        representative_id: representativeId,
      },
      { signal },
    )
    .then((res) => res.data);

export function isRequestAbortError(error) {
  if (!error) return false;
  if (error.name === "AbortError" || error.name === "CanceledError") return true;
  if (error.code === "ERR_CANCELED") return true;
  return false;
}
